import { useParams } from "react-router-dom"
import { PathParams } from "../../../../routes/routing-path"
import { useCallback, useEffect, useMemo, useState } from "react";
import { TrimUiManager } from "../../../../trim-editor/manager/ui/trim.ui.manager";
import { TrimEditableImage } from "../../../../trim-editor/manager/image-edit/trim.editable-image";
import { TrimEditableImageManager } from "../../../../trim-editor/manager/editable-image/trim.editable-image.manager";
import { usePrintOrder } from "./use-print-order";
import { PrintImagesDataEntity } from "../../../../api/sift/front/api-print-image";
import { PrintSelectImageData } from "../print-select-images/hooks/use-print-select-images";
import { PrintDataStructure, PrintItemDataEntity, PrintProductImageDataEntity } from "../../../../models/sift/json/print-model";
import { PrintUploadProgressList } from "../../../../trim-editor/manager/image-processor/trim.image-processor.manager";
import { IMAGES_KIND } from "../../../../layout-editor/manager/image-processor/image-processor.manager";
import { useQuery } from "../../../../hooks/use-query";
import { TrimThumbManager } from "../../../../trim-editor/manager/thumb/trim.thumb.manager";
import { ApiImagesGetOne } from "../../../../api/front/images/api-images";

export type TrimThumbParam = {
  itemId: string,
  imageId: string,
  base64: string,
}

export const usePrintImages = (props?: { structure?: PrintDataStructure, ignoreInitGetThumbImages?: boolean }) => {
  const {
    kijshopCd,
    shopOrderId,
  } = useParams<PathParams>();

  const filterPattern = useCallback((editableImage: TrimEditableImage) => {
    return (shopOrderId === editableImage.shopOrderId)
      && editableImage.flags.uploaded
      && editableImage.kind === IMAGES_KIND.PRINT;
  }, [
    shopOrderId,
  ]);
  const query = useQuery();
  const [editableImages, setEditableImages] = useState<TrimEditableImage[]>(TrimEditableImageManager.ins.list.filter(filterPattern));
  const [imageList, setImages] = useState<PrintSelectImageData[]>([]);
  const [thumbTrim, setThumbTrim] = useState<TrimThumbParam[]>([]);
  const [progress, setProgress] = useState<null | { queue: number, done: number, error: number }>(null);
  const [thumbImages, setThumbImages] = useState<PrintImagesDataEntity[]>([]);

  const progressView = useMemo(() => {
    if (!progress) return '';
    const {
      queue,
      done,
      error,
    } = progress;
    if (!queue && !done && !error) return '';
    return `${(((done + error) / (queue + done + error)) * 100).toFixed(2)}`;
  }, [progress]);
  const images: PrintSelectImageData[] = useMemo(() => {
    const map = new Map<string, TrimEditableImage>();
    editableImages.forEach((img) => map.set(img.id, img));
    const result = imageList.map((v) => {
      return {
        ...v,
        editableImage: map.get(v.id),
        thumbBase64: thumbTrim.find((v2) => v2.itemId === query.get('t') && v2.imageId === v.id)?.base64 ?? '',
      }
    });
    return result;
  }, [
    imageList,
    editableImages,
    thumbTrim,
    query,
  ]);
  const { getImages, getThumbImages }  = usePrintOrder();

  const createEditableImage = useCallback((
    request: PrintImagesDataEntity,
    callback?: (ei: TrimEditableImage) => void,
  ) => {
    TrimUiManager.ins.emit('r->t:trim-add-image:restore', {
      kijshopCd,
      shopOrderId,
      onSuccess: (ei) => {
        callback?.(ei);
      },
      onError: () => {},
      data: request,
    })
  }, [
    kijshopCd,
    shopOrderId,
  ]);

  const imageUpload = useCallback((uploadFiles: File[], callback?: (editableImage: TrimEditableImage) => void) => {
    TrimUiManager.ins.emit('r->t:trim-add-image', {
      files: uploadFiles,
      kijshopCd,
      shopOrderId,
      onUploaded: (p) => callback?.(p.editableImage),
    });
  }, [
    kijshopCd,
    shopOrderId,
  ]);
  const createThumbImage = useCallback(async(
    imageId: string,
    itemData: PrintItemDataEntity,
    _images?: PrintSelectImageData[],
    isRotate?: boolean,
    ignorePushArr?: boolean,
    _directFlg?: '1' | '2',
    isCompThumb?: boolean,
  ) => {
    return new Promise<string | undefined>((resolve) => {
      const target = (_images ?? images).find((v) => {
        return v.id === imageId;
      });
      const itemId = itemData.id;
      if (!target || !itemId) {
        throw new Error('存在しません。');
      };
      const mng = new TrimThumbManager();
      const directFlg = _directFlg ?? (!isRotate ? !Number(target.rotate) ? '1' : '2' : Number(target.rotate) ? '1' : '2');
      const targetTrimThumb = thumbTrim.find((v) => v.imageId === imageId)
      if (targetTrimThumb) {
        if (!ignorePushArr) {
          setThumbTrim((prev) => {
            const targetIndex = prev.findIndex((v) => v.imageId === imageId);
            if (targetIndex > -1) {
              prev.splice(targetIndex, 1);
            }
            return [...prev];
          });
        }
      }
      mng.makeThumbOutCanvas({
        kijshopCd,
        shopOrderId,
        orderId: itemId,
        item: itemData,
        editableImgId: imageId,
        directFlg,
        isCompThumb,
      }).then((res) => {
        if (res) {
          const setData = {
            imageId,
            itemId: itemData.id,
            base64: res,
          }
          if (!ignorePushArr) {
            setThumbTrim((res) => {
              const targetIndex = res.findIndex((v) => (v.itemId === itemData.id) && (v.imageId === imageId));
              if (targetIndex > -1) {
                res.splice(targetIndex, 1, setData);
              } else {
                res.push(setData);
              }
              return [...res];
            });
          }
        }
        resolve(res);
      });
    })

  }, [
    query,
    kijshopCd, shopOrderId,
    images,
    thumbTrim,
  ]);

  const getEditableImages = useCallback((
    itemData: PrintItemDataEntity,
    callback?: (editableImage: TrimEditableImage, list: PrintImagesDataEntity[]) => void,
    callbackGetImages?: (imageDataList: PrintImagesDataEntity[]) => void,
  ) => {
    return new Promise<PrintImagesDataEntity[]>((resolve) => {
      if (!kijshopCd || !shopOrderId) {
        throw new Error('商品が選択されていません');
      }
      getImages({
        ignoreCreateEditableImage: true,
        options: {
          ignoreSystemError: false,
        },
      }).then((res) => {
        const list = res.fileList ?? [];
        callbackGetImages?.(list);
        const mergeList: PrintSelectImageData[] = [];
        list.forEach((v) => {
          const targetImageList = itemData.images;
          if (!targetImageList) throw new Error('item is not found !!');
          const targetImage: Partial<PrintProductImageDataEntity> = targetImageList.find((img) => img.id === v.id) ?? {}
          if (!targetImage.id) {
            if (itemData.productData?.meta?.autoTrim) {
              targetImage.trimmingStatus = 3;
            }
            if (itemData.productData?.meta?.datePrint) {
              targetImage.datePrint = true;
            }
            targetImage.rotate = (() => {
              if ((itemData?.templates?.length ?? 0) <= 1) {
                return '0'
              }
              const isLongVerticalImg = v.width <= v.height;
              return isLongVerticalImg ? '0' : '1';
            })();
            targetImage.id = v.id;
          }
          const data = { ...v, ...targetImage };
          mergeList.push(data);
          createEditableImage(v, (ei) => {
            callback?.(ei, mergeList);
          });
        });
        setImages(mergeList);
        resolve(res.fileList);
      });
    });
  }, [
    kijshopCd,
    shopOrderId,
    getImages,
    createEditableImage,
  ]);
  useEffect(() => {
    if (!props?.ignoreInitGetThumbImages) {
      getThumbImages().then((res) => {
        setThumbImages(res.fileList);
      });
    }
  }, [])
  useEffect(() => {
    const set = (param: { list: TrimEditableImage[] }) => {
      setEditableImages(param.list.filter(filterPattern));
    };
    const setProgressData = (param: Map<string, PrintUploadProgressList>) => {
      const target = param.get(shopOrderId);
      const {
        queue = [],
        complete = [],
        error = [],
      } = target ?? {};
      if (progress && !target) {
        setProgress({ queue: 0, done: progress.done + progress.queue, error: progress.error });
        window.setTimeout(() => {
          setProgress({ queue: queue.length, done: complete.length, error: error.length });
        }, 3000)
      } else {
        console.log({ queue: queue.length, done: complete.length, error: error.length });
        setProgress({ queue: queue.length, done: complete.length, error: error.length });
      }
    }

    const setUploadImage = (param: { editableImage: TrimEditableImage }) => {
      const {
        editableImage,
      } = param;
      window.setTimeout(() => {
        getImages().then(async(res) => {
          const target = res.fileList.find((v) => v.id === editableImage.id);
          if (!imageList.find((v) => v.id === editableImage.id) && target) {
            const pushData: PrintSelectImageData = {...target};
            if (props?.structure) {
              const itemId = query.get('t');
              const itemData = props.structure.item?.[itemId ?? ''];
              if (itemData) {
                if (itemData.productData?.meta?.autoTrim) {
                  pushData.trimmingStatus = 3;
                }
                if (itemData.productData?.meta?.datePrint) {
                  pushData.datePrint = true;
                }
                pushData.rotate = (() => {
                  if ((itemData.templates?.length ?? 0) <= 1) {
                    return '0'
                  }
                  const isLongVerticalImg = pushData.width <= pushData.height;
                  return isLongVerticalImg ? '0' : '1';
                })();
                setImages((prev) => {
                  const index = prev.findIndex((v) => v.id === pushData.id)
                  if (index > -1) {
                    prev.splice(index, 1, pushData);
                    createThumbImage(pushData.id, {...itemData, images: [...itemData.images, pushData as any]}, [...prev]);
                    return [...prev];
                  } else {
                    createThumbImage(pushData.id, {...itemData, images: [...itemData.images, pushData as any]}, [...prev, pushData]);
                    return [...prev, pushData];
                  }
                });
              }
            }
          }
        })
      }, 10);
    }
    TrimUiManager.ins.on('t->r:change-editable-image-list', set);
    TrimUiManager.ins.on('r->t:get-current-queue-count', setProgressData);
    TrimUiManager.ins.on('t->r:uploaded-editable-image', setUploadImage);
    return (() => {
      TrimUiManager.ins.off('t->r:change-editable-image-list', set);
      TrimUiManager.ins.off('r->t:get-current-queue-count', setProgressData);
      TrimUiManager.ins.off('t->r:uploaded-editable-image', setUploadImage);
    });
  }, [
    shopOrderId,
    kijshopCd,
    query,
    images,
    props?.structure,
  ]);

  return {
    getEditableImages,
    images,
    setImages,
    uploadProgress: progress,
    uploadProgressView: progressView,
    thumbImages,
    imageUpload,
    createThumbImage,
    getThumbImages,
  }
}
